import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'
import { Space, Typography } from 'antd'
import styled from 'styled-components'
import { CloudUploadOutlined } from '@ant-design/icons'
import { colours, sizes, LightThemeProvider } from '@qflow/theme'

import { Sidebar } from 'portal/components/Sidebar'
import { ProjectConnectionsSummaryTable } from 'portal/components/ProjectConnectionsSummary'
import { ProjectSettingsTable } from 'portal/components/ProjectSettingsTable'
import { AlertSettings } from 'portal/components/Settings/AlertSettings'
import { Files } from 'portal/components/Settings/Files'
import { Integrations } from 'portal/components/Settings/Integrations'
import { ColourButton, Flex, icons, Spacer } from 'portal/lib/primitives'
import { PageContainer } from 'portal/pages/PageContainer'

import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { projectConnectionsSummaryLoad } from 'portal/store/projectConnectionsSummary/actions'
import { projectsGetSaga } from 'portal/store/projects/actions'

const { Title } = Typography

export const ProjectSettings = () => {
  const [view, setView] = useState('users')

  const dispatch = useDispatch()
  const { accountId, projectId } = useParams()

  const projects = useSelector(state => state.projects)
  const currentProject = projects.projects?.find(p => p.projectId === projectId)

  useEffect(() => {
    dispatch(projectConnectionsSummaryLoad(accountId, projectId))
  }, [accountId, dispatch, projectId])

  // If querystring has view, set that as the view
  const [searchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.has('view')) {
      setView(searchParams.get('view'))
    }
  }, [searchParams])

  const changeView = newView => {
    setView(newView)

    const VIEW_CHANGED = 'frontend_project_settings_view_changed'
    dispatch(
      trackMixpanel(VIEW_CHANGED, {
        view: newView,
        account_id: accountId,
        project_id: projectId
      })
    )
  }

  const projectConnectionsSummary = useSelector(
    state => state.projectConnectionsSummary
  )

  if (!currentProject) {
    return ''
  }

  return (
    <PageContainer id="ProjectSettings">
      <LightThemeProvider>
        <Sidebar />
        <Space direction="vertical" size="middle">
          <Flex.Row
            style={{
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              justifyContent: 'space-between'
            }}
          >
            <div>
              <Title level={3}>{currentProject.name} Settings</Title>
            </div>
          </Flex.Row>
          <FlexWrapperPanel>
            <FlexWrapper style={{ gap: '0.125rem' }}>
              <DataViewButtons
                id="usersButton"
                className={view === 'users' ? 'active' : ''}
                onClick={() => {
                  changeView('users')
                }}
              >
                <icons.Person colour="currentColor" size={sizes.SMALL_MEDIUM} />
                <Spacer.Fine />
                Users
              </DataViewButtons>
              <DataViewButtons
                id="projectDataButton"
                className={view === 'projectData' ? 'active' : ''}
                onClick={() => {
                  changeView('projectData')
                }}
              >
                <icons.Data colour="currentColor" size={sizes.SMALL_MEDIUM} />
                <Spacer.Fine />
                Project Data
              </DataViewButtons>
              <DataViewButtons
                id="alertSettingsButton"
                className={view === 'alertSettings' ? 'active' : ''}
                onClick={() => {
                  changeView('alertSettings')
                }}
              >
                <icons.ShieldTick
                  colour="currentColor"
                  size={sizes.SMALL_MEDIUM}
                />
                <Spacer.Fine />
                Alert Settings
              </DataViewButtons>
              <DataViewButtons
                id="integrationsButton"
                className={view === 'integrations' ? 'active' : ''}
                onClick={() => {
                  changeView('integrations')
                }}
              >
                <CloudUploadOutlined style={{ fontSize: sizes.MEDIUM }} />
                <Spacer.Fine />
                Integrations
              </DataViewButtons>
              <DataViewButtons
                id="filesButton"
                className={view === 'files' ? 'active' : ''}
                onClick={() => {
                  changeView('files')
                }}
              >
                <icons.Document
                  colour="currentColor"
                  size={sizes.SMALL_MEDIUM}
                />
                <Spacer.Fine />
                Files
              </DataViewButtons>
            </FlexWrapper>
          </FlexWrapperPanel>
          {view === 'users' && <ProjectSettingsTable />}
          {view === 'projectData' && (
            <ProjectConnectionsSummaryTable
              summary={projectConnectionsSummary.projectConnectionsSummary}
            />
          )}
          {view === 'alertSettings' && (
            <AlertSettings project={currentProject} />
          )}
          {view === 'integrations' && (
            <Integrations
              project={currentProject}
              projectsReloader={projectsGetSaga}
            />
          )}
          {view === 'files' && <Files project={currentProject} />}
        </Space>
      </LightThemeProvider>
    </PageContainer>
  )
}

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`
const FlexWrapperPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const DataViewButtons = styled(ColourButton.BasicBorderless)`
  font-weight: 500;

  &.active {
    background-color: ${colours.CORE_NAVY};
    color: ${colours.WHITE};
  }
`
